// src/pages/project/ProjectSettingsLabelsPage.tsx
// Labels settings: ListLabels + Create / Update / Delete.

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { observer } from '@react';
import { useProject, useProjectLabel, useI18n } from '@react/hooks';
import { Button, Input, Card, CardContent, ErrorAlert } from '@react/components';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ColorPicker } from '@/components/ColorPicker';
import { ProjectModal, Field } from '@/features/project/ProjectModal';
import { entityRef, idOf, listParams } from '@/features/project/meta';
import { PROJECT_ACCENT_COLORS } from '@/features/project/logic';
import type { Entity } from '@core/types/entity';
import type { ProjectLabel } from '@core/types/project';

export const ProjectSettingsLabelsPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const projectStore = useProject();
  const store = useProjectLabel();
  const { t } = useI18n();

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<ProjectLabel | null>(null);
  const [deleting, setDeleting] = useState<ProjectLabel | null>(null);
  const [busy, setBusy] = useState(false);

  const projectEntity = (projectStore.currentItem as never as { entity?: Entity } | null)?.entity;

  const reload = () => id && store.ListLabels(id, listParams());
  useEffect(() => {
    if (id && !projectStore.currentItem) projectStore.GetProject(id);
    reload();
  }, [id]);

  const labels: ProjectLabel[] = store.items ?? [];

  const del = async () => {
    if (!id || !deleting) return;
    setBusy(true);
    try {
      await store.DeleteLabel(id, idOf(deleting));
      setDeleting(null);
      reload();
    } catch {
      /* store surfaces error */
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-foreground">{t('project.section.labels')}</h2>
          <p className="mt-0.5 text-sm text-muted-foreground">{t('project.section.labelsDesc')}</p>
        </div>
        <Button
          size="sm"
          onClick={() => {
            setEditing(null);
            setShowForm(true);
          }}
        >
          {t('project.label.create')}
        </Button>
      </div>

      {!!store.error && <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />}

      {labels.length === 0 ? (
        <p className="py-8 text-center text-sm text-muted-foreground">{t('project.label.empty')}</p>
      ) : (
        <Card>
          <CardContent className="flex flex-wrap gap-2 p-4">
            {labels.map((l) => (
              <div key={idOf(l)} className="flex items-center gap-1 rounded-full border border-border py-0.5 pl-2 pr-1">
                <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: l.color || undefined }} aria-hidden="true" />
                <span className="text-sm text-foreground">{l.name}</span>
                <button
                  type="button"
                  onClick={() => {
                    setEditing(l);
                    setShowForm(true);
                  }}
                  aria-label={t('common.edit')}
                  className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <path d="M11 2l3 3-8 8-4 1 1-4z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={() => setDeleting(l)}
                  aria-label={t('common.delete')}
                  className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                  </svg>
                </button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {showForm && projectEntity && (
        <LabelFormDialog
          projectEntity={projectEntity}
          label={editing}
          onClose={() => setShowForm(false)}
          onSaved={() => {
            setShowForm(false);
            reload();
          }}
        />
      )}

      <ConfirmDialog
        open={!!deleting}
        title={t('project.label.deleteTitle')}
        description={t('project.label.deleteDesc')}
        destructive
        isConfirming={busy}
        onConfirm={del}
        onCancel={() => setDeleting(null)}
      />
    </div>
  );
});

function LabelFormDialog({
  projectEntity,
  label,
  onClose,
  onSaved,
}: {
  projectEntity: Entity;
  label: ProjectLabel | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useI18n();
  const store = useProjectLabel();
  const [name, setName] = useState(label?.name ?? '');
  const [color, setColor] = useState(label?.color ?? '#6366f1');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      if (label) {
        await store.UpdateLabel(projectEntity.id, idOf(label), {
          project: projectEntity,
          label: label.entity ?? entityRef(idOf(label), 'project_label', projectEntity),
          name: name.trim(),
          color,
        } as never);
      } else {
        await store.CreateLabel(projectEntity.id, {
          project: projectEntity,
          name: name.trim(),
          color,
        } as never);
      }
      onSaved();
    } catch {
      /* store surfaces error */
    } finally {
      setBusy(false);
    }
  };

  return (
    <ProjectModal
      open
      title={label ? t('project.label.editTitle') : t('project.label.createTitle')}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button size="sm" onClick={submit} disabled={busy || !name.trim()}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {!!store.error && <ErrorAlert error={store.error} />}
        <Field label={t('project.label.name')} htmlFor="label-name">
          <Input id="label-name" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label={t('project.label.color')}>
          <ColorPicker options={PROJECT_ACCENT_COLORS} value={color} onChange={setColor} />
        </Field>
      </div>
    </ProjectModal>
  );
}

export default ProjectSettingsLabelsPage;
